import { useEffect, useState } from 'react';
import {
  clearLines,
  diagEnabled,
  dump,
  getLines,
  log,
  showConsole,
  subscribe,
} from '../lib/diag.ts';

/**
 * The diagnostics panel behind `?debug=1`: a bug button in the corner that opens the log, with
 * Copy (to paste into a message), Console (the on-device inspector), Clear and Close. Renders
 * nothing when diagnostics are off.
 */
export function DiagPanel() {
  const [open, setOpen] = useState(false);
  const [, bump] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => subscribe(() => bump((n) => n + 1)), []);
  if (!diagEnabled()) return null;

  const copy = async () => {
    const text = dump();
    try {
      await navigator.clipboard.writeText(text);
      setCopied('Copied');
    } catch {
      // No clipboard permission (older iOS): leave the text selected for a manual copy.
      const ta = document.getElementById('diag-text') as HTMLTextAreaElement | null;
      ta?.focus();
      ta?.select();
      setCopied('Select all and copy');
    }
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <>
      <button
        type="button"
        aria-label="Diagnostics"
        data-testid="diag-open"
        className="fixed bottom-2 left-2 z-[60] flex h-10 w-10 items-center justify-center rounded-full bg-ink/80 text-lg text-white shadow-lg"
        onClick={() => {
          log('diag', open ? 'panel closed' : 'panel opened');
          setOpen((o) => !o);
        }}
      >
        🐞
      </button>
      {open && (
        <div
          className="fixed inset-x-2 bottom-14 top-2 z-[60] flex flex-col gap-2 rounded-2xl bg-white p-3 text-ink shadow-2xl"
          data-testid="diag-panel"
        >
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-bold">Diagnostics</span>
            <span className="text-muted">{getLines().length} lines</span>
            <span className="flex-1" />
            <button type="button" className="btn btn-primary btn-sm" onClick={() => void copy()}>
              {copied ?? 'Copy'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={showConsole}>
              Console
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearLines}>
              Clear
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
          <textarea
            id="diag-text"
            readOnly
            className="min-h-0 flex-1 resize-none rounded-xl bg-paper p-2 font-mono text-[11px] leading-snug"
            value={dump()}
          />
        </div>
      )}
    </>
  );
}
